// src/components/Viewer.jsx
// Fixes:
//   1. handleRecalibrate uses sub.canScan (not hardcoded lifetimeScansUsed >= 4)
//   2. Scan history reads renderUrl || lastScanUrl (aligned with renderHair.js)
//   3. Paywall opens with correct reason from sub.canScan.reason
//   4. formatNaira from useSubscription (not needed here but hook is used correctly)
//   5. All original layout, design and 3D logic preserved exactly

import React, { Suspense, useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { Canvas, useLoader, useFrame } from '@react-three/fiber';
import {
  OrbitControls,
  ContactShadows,
  useGLTF,
  Stage
} from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import * as THREE from 'three';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useSubscription } from '../hooks/useSubscription';
import Paywall from './Paywall';

const GOLD          = '#B8860B';
const CLINICAL_GREEN = '#2ecc71';
const FALLBACK_HEAD  = '/models/default-head.glb';

// ── Synced camera ─────────────────────────────────────────────────────────────
function Controls({ controlsRef, otherRef }) {
  useFrame(() => {
    if (controlsRef.current && otherRef?.current) {
      otherRef.current.object.position.copy(controlsRef.current.object.position);
      otherRef.current.object.quaternion.copy(controlsRef.current.object.quaternion);
      otherRef.current.target.copy(controlsRef.current.target);
      otherRef.current.update();
    }
  });
  return (
    <OrbitControls
      ref={controlsRef}
      enablePan={false}
      minDistance={2}
      maxDistance={6}
      minPolarAngle={Math.PI / 4}
      maxPolarAngle={Math.PI / 1.6}
    />
  );
}

// ── Biometric scene ───────────────────────────────────────────────────────────
function SceneLayer({ headUrl, hairUrl, diagnosticsActive }) {
  const headRef = useRef();
  const hairRef = useRef();

  const isHeadOBJ = headUrl?.toLowerCase().endsWith('.obj');
  const isHairOBJ = hairUrl?.toLowerCase().endsWith('.obj');

  const headData = isHeadOBJ
    ? useLoader(OBJLoader, headUrl)
    : useGLTF(headUrl || FALLBACK_HEAD);

  const hairData = hairUrl
    ? (isHairOBJ ? useLoader(OBJLoader, hairUrl) : useGLTF(hairUrl))
    : null;

  const headRoot = isHeadOBJ ? headData : headData.scene;
  const hairRoot = hairData ? (isHairOBJ ? hairData : hairData.scene) : null;

  useMemo(() => {
    if (headRoot) {
      headRoot.traverse((child) => {
        if (child.isMesh) {
          child.material = new THREE.MeshStandardMaterial({
            color:            diagnosticsActive ? '#111' : '#8c8c8c',
            roughness:        0.7,
            metalness:        0.1,
            wireframe:        diagnosticsActive,
            emissive:         diagnosticsActive ? CLINICAL_GREEN : '#000',
            emissiveIntensity: diagnosticsActive ? 0.5 : 0,
          });
          child.castShadow    = true;
          child.receiveShadow = true;
        }
      });
    }
    if (hairRoot) {
      hairRoot.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.transparent = true;
          child.material.opacity     = diagnosticsActive ? 0.4 : 1.0;
        }
      });
    }
  }, [headRoot, hairRoot, diagnosticsActive]);

  useLayoutEffect(() => {
    if (headRef.current && hairRef.current) {
      const headBox  = new THREE.Box3().setFromObject(headRef.current);
      const headSize = new THREE.Vector3();
      headBox.getSize(headSize);
      const headCenter = new THREE.Vector3();
      headBox.getCenter(headCenter);
      hairRef.current.position.y = headCenter.y + (headSize.y * 0.08);
      const scaleFactor = headSize.x * 1.01;
      hairRef.current.scale.set(scaleFactor, scaleFactor, scaleFactor);
    }
  }, [headRoot, hairRoot]);

  return (
    <group>
      {headRoot && <primitive ref={headRef} object={headRoot} position={[0, -1, 0]} />}
      {hairRoot && <primitive ref={hairRef} object={hairRoot} />}
    </group>
  );
}

const LoadingScreen = () => (
  <div style={loadingOverlay}>
    <div style={spinner} />
    <p style={{ letterSpacing: '4px', fontSize: '0.7rem' }}>DECODING CRANIAL GEOMETRY...</p>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
export default function Viewer() {
  const { styleId }  = useParams();
  const location     = useLocation();
  const navigate     = useNavigate();
  const containerRef = useRef();
  const controls1    = useRef();
  const controls2    = useRef();

  const [scanUrl,          setScanUrl]          = useState(null);
  const [compareUrl,       setCompareUrl]       = useState(null);
  const [styleUrl,         setStyleUrl]         = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [diagnosticsActive,setDiagnosticsActive] = useState(false);
  const [historyOpen,      setHistoryOpen]      = useState(false);
  const [isCompareMode,    setIsCompareMode]    = useState(false);
  const [scanHistory,      setScanHistory]      = useState([]);
  const [showPaywall,      setShowPaywall]      = useState(false);
  const [paywallReason,    setPaywallReason]    = useState(null);

  const sub             = useSubscription();
  const targetClientUid = location.state?.targetClientUid;

  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      try {
        const activeUid = targetClientUid || auth.currentUser?.uid;
        if (!activeUid) { navigate('/login'); return; }

        const userSnap = await getDoc(doc(db, 'users', activeUid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          // FIX 2: aligned with renderHair.js field names
          const defaultUrl = userData.lastScanUrl || userData.initialRenderUrl || FALLBACK_HEAD;
          setScanUrl(defaultUrl);
          setCompareUrl(defaultUrl);

          // FIX 2: read from ai_scans collection (written by renderHair)
          try {
            const scansQ   = query(collection(db, 'ai_scans'), orderBy('createdAt', 'desc'));
            const scansSnap = await getDocs(scansQ);
            const filtered  = scansSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(s => s.userId === activeUid);
            setScanHistory(filtered);
          } catch {
            // ai_scans may not exist yet — silent fail
          }
        }

        if (styleId) {
          const projSnap = await getDoc(doc(db, 'projections', styleId));
          if (projSnap.exists()) {
            setStyleUrl(projSnap.data().model3DUrl);
          } else {
            const styleSnap = await getDoc(doc(db, 'global_gallery', styleId));
            if (styleSnap.exists()) setStyleUrl(styleSnap.data().model3DUrl);
          }
        }
      } catch (err) {
        console.error('Vault Access Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, [styleId, targetClientUid, navigate]);

  const handleExport = useCallback(() => {
    const canvases = containerRef.current.querySelectorAll('canvas');
    canvases.forEach((canvas, index) => {
      const link = document.createElement('a');
      link.setAttribute('download', `Cranial_Snapshot_${index + 1}.png`);
      link.setAttribute('href', canvas.toDataURL('image/png'));
      link.click();
    });
  }, []);

  // FIX 1: use sub.canScan instead of hardcoded threshold
  const handleRecalibrate = () => {
    if (!sub.loading && !sub.canScan.allowed) {
      setPaywallReason(sub.canScan.reason || 'free_exhausted');
      setShowPaywall(true);
      return;
    }
    navigate('/scan?force=true');
  };

  if (loading) return <LoadingScreen />;

  return (
    <div ref={containerRef} style={viewerWrapper}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        canvas { outline: none; }
      `}</style>

      {showPaywall && (
        <Paywall
          reason={paywallReason}
          onClose={() => setShowPaywall(false)}
          redirectAfter="/viewer"
        />
      )}

      <button onClick={() => navigate(-1)} style={backBtn}>← EXIT BIOMETRIC VAULT</button>

      <div style={topControls}>
        <button onClick={handleExport} style={toggleBtn}>📸 EXPORT REPORT</button>
        <button
          onClick={() => setIsCompareMode(!isCompareMode)}
          style={{ ...toggleBtn, background: isCompareMode ? GOLD : 'rgba(0,31,63,0.8)', color: isCompareMode ? '#000' : GOLD }}
        >
          {isCompareMode ? '📊 EXIT COMPARISON' : '📊 COMPARE SCANS'}
        </button>
        <button onClick={() => setHistoryOpen(!historyOpen)} style={toggleBtn}>
          {historyOpen ? '✕ CLOSE LOGS' : '⏳ SCAN HISTORY'}
        </button>
        <button onClick={handleRecalibrate} style={recalibrateBtn}>
          RECALIBRATE SCAN {!sub.canScan.allowed && !sub.isSuperAdmin ? '🔒' : ''}
        </button>
      </div>

      {/* HISTORY DRAWER */}
      <div style={{ ...historyDrawer, right: historyOpen ? 0 : '-350px' }}>
        <h3 style={drawerTitle}>CRANIAL REPOSITORY</h3>
        <div style={drawerList}>
          {scanHistory.length === 0 && (
            <p style={{ color: '#444', fontSize: '0.7rem', textAlign: 'center' }}>No scan history yet.</p>
          )}
          {scanHistory.map((scan) => (
            <div
              key={scan.id}
              onClick={() => {
                const url = scan.renderUrl || scan.lastScanUrl || FALLBACK_HEAD;
                isCompareMode ? setCompareUrl(url) : setScanUrl(url);
              }}
              style={historyCard}
            >
              <div style={cardDate}>
                {scan.createdAt?.toDate ? scan.createdAt.toDate().toLocaleDateString() : 'Unknown'}
              </div>
              <div style={cardType}>{scan.modelType || 'Full Cranial'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CANVAS(ES) */}
      <div style={{ ...canvasContainer, flexDirection: isCompareMode ? 'row' : 'column' }}>
        <div style={viewport}>
          {isCompareMode && <div style={viewLabel}>PRIMARY DATA</div>}
          <Suspense fallback={<LoadingScreen />}>
            <Canvas shadows gl={{ preserveDrawingBuffer: true }} camera={{ position: [0, 0, 4], fov: 35 }}>
              <Stage environment="studio" intensity={0.5} adjustCamera={false}>
                <SceneLayer headUrl={scanUrl} hairUrl={styleUrl} diagnosticsActive={diagnosticsActive} />
              </Stage>
              <Controls controlsRef={controls1} otherRef={controls2} />
              <ContactShadows opacity={0.4} scale={10} blur={2.5} far={4} color="#000" />
            </Canvas>
          </Suspense>
        </div>

        {isCompareMode && (
          <div style={{ ...viewport, borderLeft: `1px solid ${GOLD}44` }}>
            <div style={viewLabel}>COMPARISON DATA</div>
            <Suspense fallback={<LoadingScreen />}>
              <Canvas shadows gl={{ preserveDrawingBuffer: true }} camera={{ position: [0, 0, 4], fov: 35 }}>
                <Stage environment="studio" intensity={0.5} adjustCamera={false}>
                  <SceneLayer headUrl={compareUrl} hairUrl={styleUrl} diagnosticsActive={diagnosticsActive} />
                </Stage>
                <Controls controlsRef={controls2} otherRef={controls1} />
                <ContactShadows opacity={0.4} scale={10} blur={2.5} far={4} color="#000" />
              </Canvas>
            </Suspense>
          </div>
        )}
      </div>

      {/* INFO PLATE */}
      <div style={infoPlate}>
        <div style={{
          ...statusDot,
          background:  diagnosticsActive ? CLINICAL_GREEN : '#2ecc71',
          boxShadow:   diagnosticsActive ? `0 0 15px ${CLINICAL_GREEN}` : '0 0 10px #2ecc71',
        }} />
        <h2 style={plateTitle}>
          {isCompareMode ? 'DIFFERENTIAL ANALYSIS' : targetClientUid ? 'CLIENT CRANIAL BLUEPRINT' : 'PERSONAL CRANIAL SOLID'}
        </h2>
        <button
          onClick={() => setDiagnosticsActive(!diagnosticsActive)}
          style={{ ...diagToggle, color: diagnosticsActive ? CLINICAL_GREEN : '#fff', borderColor: diagnosticsActive ? CLINICAL_GREEN : 'rgba(255,255,255,0.2)' }}
        >
          {diagnosticsActive ? '● DIAGNOSTICS ACTIVE' : '○ ENABLE HEALTH OVERLAY'}
        </button>
        <p style={plateSub}>
          {targetClientUid ? `SECURE SESSION ID: ${targetClientUid.substring(0, 8)}` : 'BIOMETRIC INTEGRITY VERIFIED'}
        </p>

        {/* Subscription status strip */}
        {!sub.loading && !sub.isSuperAdmin && (
          <p style={{ fontSize: '0.58rem', marginTop: '8px', color: sub.isSubscriptionActive ? GOLD : 'rgba(255,255,255,0.4)' }}>
            {sub.isSubscriptionActive
              ? `${sub.tier?.toUpperCase()} · ${sub.monthlyScansUsed}/${sub.PRICING[sub.tier]?.scansPerMonth} scans this month`
              : `FREE · ${sub.lifetimeScansUsed}/1 scan used`}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const viewerWrapper  = { width:'100%', height:'100vh', background:'#010810', overflow:'hidden', position:'relative' };
const canvasContainer = { display:'flex', width:'100%', height:'100vh' };
const viewport       = { flex:1, position:'relative' };
const viewLabel      = { position:'absolute', top:100, left:'50%', transform:'translateX(-50%)', color:GOLD, fontSize:'0.55rem', fontWeight:'900', letterSpacing:'2px', zIndex:5, background:'rgba(0,0,0,0.7)', padding:'6px 12px', borderRadius:'4px', border:`1px solid ${GOLD}44` };
const topControls    = { position:'absolute', top:30, right:30, zIndex:10, display:'flex', gap:'10px', flexWrap:'wrap', justifyContent:'flex-end' };
const toggleBtn      = { background:'rgba(0,31,63,0.8)', color:GOLD, border:`1px solid ${GOLD}44`, padding:'12px 24px', borderRadius:'10px', fontWeight:'900', cursor:'pointer', fontSize:'0.65rem', backdropFilter:'blur(10px)' };
const recalibrateBtn = { background:GOLD, color:'#000', border:'none', padding:'12px 24px', borderRadius:'10px', fontWeight:'900', cursor:'pointer', fontSize:'0.65rem' };
const historyDrawer  = { position:'absolute', top:0, height:'100vh', width:'320px', background:'rgba(5,10,15,0.95)', zIndex:100, transition:'right 0.4s ease', borderLeft:'1px solid #111', padding:'100px 20px', backdropFilter:'blur(20px)' };
const historyCard    = { padding:'15px', borderRadius:'10px', border:'1px solid rgba(184,134,11,0.2)', marginBottom:'10px', cursor:'pointer', background:'#050a0f' };
const cardDate       = { color:'#fff', fontSize:'0.7rem', fontWeight:'bold' };
const cardType       = { color:GOLD, fontSize:'0.5rem', fontWeight:'900', marginTop:'5px' };
const backBtn        = { position:'absolute', top:30, left:30, zIndex:10, background:GOLD, color:'black', border:'none', padding:'14px 28px', borderRadius:'12px', fontWeight:'900', cursor:'pointer', fontSize:'0.7rem' };
const infoPlate      = { position:'absolute', bottom:40, left:'50%', transform:'translateX(-50%)', background:'rgba(0,31,63,0.85)', padding:'20px 40px', borderRadius:'25px', border:`1px solid ${GOLD}44`, textAlign:'center', backdropFilter:'blur(20px)', zIndex:5, minWidth:'320px' };
const plateTitle     = { color:GOLD, margin:'0 0 10px 0', fontSize:'0.9rem', fontWeight:'900', letterSpacing:'2px' };
const plateSub       = { opacity:0.5, color:'#fff', fontSize:'0.6rem', marginTop:'6px', fontWeight:'bold' };
const diagToggle     = { background:'transparent', border:'1px solid', padding:'6px 15px', borderRadius:'8px', fontSize:'0.6rem', fontWeight:'800', cursor:'pointer', letterSpacing:'1px', marginBottom:'5px' };
const loadingOverlay = { height:'100vh', width:'100vw', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', background:'#000', color:GOLD, position:'absolute', zIndex:100 };
const spinner        = { width:'40px', height:'40px', border:'2px solid rgba(184,134,11,0.1)', borderTop:`2px solid ${GOLD}`, borderRadius:'50%', animation:'spin 0.8s linear infinite', marginBottom:'20px' };
const drawerTitle    = { color:GOLD, fontSize:'0.7rem', letterSpacing:'2px', marginBottom:'20px', fontWeight:'900' };
const drawerList     = { overflowY:'auto', maxHeight:'70vh' };
const statusDot      = { width:'8px', height:'8px', borderRadius:'50%', margin:'0 auto 10px', transition:'all 0.3s ease' };
